import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "components/ui/command";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Plus } from "@phosphor-icons/react";
import {
  AssetCategory,
  AssetType,
  AssetScope,
  AssetStatus,
  MaintenanceScheduleTemplate,
} from "shared/types/asset";
import {
  useCreateAsset,
  useCreateBulkAssets,
  useCreateAssetCategory,
  useCreateAssetType,
} from "hooks/useAssets";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { TemplateConflictDialog } from "./TemplateConflictDialog";

interface AddAssetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: AssetCategory[];
  buildingId: string;
  unitId?: string;
  units?: { id: string; boxNumber?: string }[];
  onSuccess: () => void;
}

const SCOPE_LABELS: Record<AssetScope, string> = {
  [AssetScope.BUILDING]: "Gebouw",
  [AssetScope.UNIT]: "Unit",
  [AssetScope.ROOM]: "Kamer",
  [AssetScope.PORTABLE]: "Draagbaar",
};

type DialogStep = "select-type" | "create-category" | "create-type" | "create-asset";

export const AddAssetDialog = ({
  open,
  onOpenChange,
  categories,
  buildingId,
  unitId: fixedUnitId,
  units = [],
  onSuccess,
}: AddAssetDialogProps) => {
  const [step, setStep] = useState<DialogStep>("select-type");
  const [selectedType, setSelectedType] = useState<{
    type: AssetType;
    category: AssetCategory;
  } | null>(null);

  // Asset fields
  const [name, setName] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [selectedUnitId, setSelectedUnitId] = useState("");

  // New category fields
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryDescription, setNewCategoryDescription] = useState("");

  // New type fields
  const [newTypeName, setNewTypeName] = useState("");
  const [newTypeDescription, setNewTypeDescription] = useState("");
  const [newTypeScopes, setNewTypeScopes] = useState<AssetScope[]>([AssetScope.BUILDING]);
  const [newTypeCategoryId, setNewTypeCategoryId] = useState<number | null>(null);

  // Conflict resolution state
  const [conflictData, setConflictData] = useState<{
    assetId: string;
    assetName: string;
    conflicts: MaintenanceScheduleTemplate[];
  } | null>(null);

  const { createAsset, isCreating } = useCreateAsset();
  const { createBulkAssets, isCreating: isBulkCreating } = useCreateBulkAssets();
  const { createCategory, isCreating: isCategoryCreating } = useCreateAssetCategory();
  const { createAssetType, isCreating: isTypeCreating } = useCreateAssetType();

  const handleSelectType = (item: { type: AssetType; category: AssetCategory }) => {
    setSelectedType(item);
    setName(item.type.name);
    setStep("create-asset");
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;

    try {
      const created = await createCategory({
        name: newCategoryName.trim(),
        description: newCategoryDescription.trim() || undefined,
      });
      toast.success(`Categorie "${created.name}" aangemaakt`);
      // After creating category, go to create type with this category pre-selected
      setNewTypeCategoryId(created.id);
      setNewCategoryName("");
      setNewCategoryDescription("");
      setStep("create-type");
      onSuccess(); // Refresh categories list
    } catch (error) {
      toast.error("Kon categorie niet aanmaken");
    }
  };

  const handleCreateType = async () => {
    if (!newTypeName.trim() || !newTypeCategoryId) return;

    try {
      const created = await createAssetType({
        assetCategoryId: newTypeCategoryId,
        name: newTypeName.trim(),
        description: newTypeDescription.trim() || undefined,
        scopes: newTypeScopes,
      });

      const category = categories.find((c) => c.id === newTypeCategoryId);
      toast.success(`Type "${created.name}" aangemaakt`);

      // Auto-select the newly created type and go to asset creation
      if (category) {
        setSelectedType({ type: created, category });
      }
      setName(created.name);
      setNewTypeName("");
      setNewTypeDescription("");
      setStep("create-asset");
      onSuccess(); // Refresh categories list
    } catch (error) {
      toast.error("Kon type niet aanmaken");
    }
  };

  const handleSubmit = async () => {
    if (!selectedType) return;

    try {
      const resolvedUnitId =
        fixedUnitId ||
        (selectedType.type.scopes?.includes(AssetScope.UNIT) ? selectedUnitId : undefined);

      if (quantity > 1) {
        const assets = Array.from({ length: quantity }, (_, i) => ({
          id: uuidv4(),
          assetCategoryId: selectedType.category.id,
          assetTypeId: selectedType.type.id,
          name: `${name} ${i + 1}`,
          buildingId,
          ...(resolvedUnitId ? { unitId: resolvedUnitId } : {}),
          status: AssetStatus.ACTIVE,
          archived: false,
        }));
        await createBulkAssets(assets);
        toast.success(`${quantity} voorzieningen aangemaakt`);
      } else {
        const result = await createAsset({
          id: uuidv4(),
          assetCategoryId: selectedType.category.id,
          assetTypeId: selectedType.type.id,
          name,
          buildingId,
          ...(resolvedUnitId ? { unitId: resolvedUnitId } : {}),
          status: AssetStatus.ACTIVE,
          archived: false,
        });

        // Check for maintenance template conflicts
        if (result.maintenanceTemplateConflicts?.length) {
          toast.success("Voorziening aangemaakt");
          onSuccess();
          handleClose();
          setConflictData({
            assetId: result.id,
            assetName: result.name,
            conflicts: result.maintenanceTemplateConflicts,
          });
          return;
        }

        toast.success("Voorziening aangemaakt");
      }
      onSuccess();
      handleClose();
    } catch (error) {
      toast.error("Kon voorziening niet aanmaken");
    }
  };

  const handleClose = () => {
    setStep("select-type");
    setSelectedType(null);
    setName("");
    setQuantity(1);
    setSelectedUnitId("");
    setNewCategoryName("");
    setNewCategoryDescription("");
    setNewTypeName("");
    setNewTypeDescription("");
    setNewTypeScopes([AssetScope.BUILDING]);
    setNewTypeCategoryId(null);
    // Don't clear conflictData here — it's shown in a separate dialog after close
    onOpenChange(false);
  };

  const isUnitScope = selectedType?.type.scopes?.includes(AssetScope.UNIT);
  const hasAnyTypes = categories.some(
    (c) => (c.assetTypes || []).some((t) => t.active)
  );

  const getTitle = () => {
    switch (step) {
      case "select-type":
        return "Voorziening toevoegen";
      case "create-category":
        return "Nieuwe categorie aanmaken";
      case "create-type":
        return "Nieuw type aanmaken";
      case "create-asset":
        return `${selectedType?.type.name || "Voorziening"} toevoegen`;
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
          {step === "select-type" && hasAnyTypes && (
            <DialogDescription>
              Selecteer een bestaand type of maak een nieuw type aan.
            </DialogDescription>
          )}
        </DialogHeader>

        {/* Step 1: Select existing type or create new */}
        {step === "select-type" && (
          <div className="space-y-3">
            {hasAnyTypes ? (
              <Command className="rounded-lg border">
                <CommandInput placeholder="Zoek een type voorziening..." />
                <CommandList className="max-h-[250px]">
                  <CommandEmpty>
                    <p className="py-3 text-center text-sm text-muted-foreground">
                      Geen types gevonden voor deze zoekopdracht
                    </p>
                  </CommandEmpty>
                  {categories.map((category) => {
                    const activeTypes = (category.assetTypes || []).filter(
                      (t) => t.active
                    );
                    if (activeTypes.length === 0) return null;
                    return (
                      <CommandGroup key={category.id} heading={category.name}>
                        {activeTypes.map((type) => (
                          <CommandItem
                            key={type.id}
                            value={`${category.name} ${type.name}`}
                            onSelect={() => handleSelectType({ type, category })}
                            className="flex items-center justify-between"
                          >
                            <span>{type.name}</span>
                            <div className="flex gap-1">
                              {type.scopes?.map((s) => (
                                <Badge key={s} variant="outline" className="text-xs">
                                  {SCOPE_LABELS[s]}
                                </Badge>
                              ))}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    );
                  })}
                </CommandList>
              </Command>
            ) : (
              <div className="rounded-lg border border-dashed p-6 text-center">
                <p className="text-sm font-medium">Nog geen voorziening types</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Maak eerst een categorie en type aan om voorzieningen toe te voegen.
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => setStep("create-category")}
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                Nieuwe categorie
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => {
                  if (categories.length > 0) {
                    setNewTypeCategoryId(categories[0].id);
                  }
                  setStep("create-type");
                }}
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                Nieuw type
              </Button>
            </div>
          </div>
        )}

        {/* Step: Create new category */}
        {step === "create-category" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Een categorie groepeert gelijkaardige voorzieningen (bv. &quot;CV &amp;
              Verwarming&quot;, &quot;Branddetectie&quot;, &quot;Liften&quot;).
            </p>

            <div className="space-y-2">
              <Label>Naam *</Label>
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="bv. CV & Verwarming"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label>Beschrijving</Label>
              <Input
                value={newCategoryDescription}
                onChange={(e) => setNewCategoryDescription(e.target.value)}
                placeholder="Optionele beschrijving"
              />
            </div>

            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep("select-type")}>
                Terug
              </Button>
              <Button
                onClick={handleCreateCategory}
                disabled={!newCategoryName.trim() || isCategoryCreating}
              >
                {isCategoryCreating ? "Aanmaken..." : "Categorie aanmaken"}
              </Button>
            </div>
          </div>
        )}

        {/* Step: Create new type */}
        {step === "create-type" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Een type is een specifieke voorziening binnen een categorie (bv.
              &quot;Gasketel&quot; in &quot;CV &amp; Verwarming&quot;).
            </p>

            <div className="space-y-2">
              <Label>Categorie *</Label>
              {categories.length > 0 ? (
                <Select
                  value={newTypeCategoryId?.toString() || ""}
                  onValueChange={(v) => setNewTypeCategoryId(parseInt(v))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecteer een categorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id.toString()}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="rounded-md border border-dashed p-3 text-center">
                  <p className="text-sm text-muted-foreground">
                    Geen categorieen beschikbaar
                  </p>
                  <Button
                    variant="link"
                    size="sm"
                    className="mt-1"
                    onClick={() => setStep("create-category")}
                  >
                    Maak eerst een categorie aan
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Naam *</Label>
              <Input
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                placeholder="bv. Gasketel"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label>Beschrijving</Label>
              <Input
                value={newTypeDescription}
                onChange={(e) => setNewTypeDescription(e.target.value)}
                placeholder="Optionele beschrijving"
              />
            </div>

            <div className="space-y-2">
              <Label>Bereik</Label>
              <div className="flex flex-wrap gap-3">
                {Object.entries(SCOPE_LABELS).map(([value, label]) => (
                  <label key={value} className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newTypeScopes.includes(value as AssetScope)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewTypeScopes([...newTypeScopes, value as AssetScope]);
                        } else {
                          setNewTypeScopes(newTypeScopes.filter((s) => s !== value));
                        }
                      }}
                      className="rounded border-input"
                    />
                    {label}
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Bepaalt waar deze voorziening kan worden toegewezen.
              </p>
            </div>

            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep("select-type")}>
                Terug
              </Button>
              <Button
                onClick={handleCreateType}
                disabled={
                  !newTypeName.trim() || !newTypeCategoryId || isTypeCreating
                }
              >
                {isTypeCreating ? "Aanmaken..." : "Type aanmaken & doorgaan"}
              </Button>
            </div>
          </div>
        )}

        {/* Step: Create asset */}
        {step === "create-asset" && selectedType && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge
                variant="secondary"
                style={{
                  backgroundColor: selectedType.category.color || undefined,
                  color: selectedType.category.color ? "#fff" : undefined,
                }}
              >
                {selectedType.category.name}
              </Badge>
              <span>{selectedType.type.name}</span>
            </div>

            <div className="space-y-2">
              <Label>Naam</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Naam van de voorziening"
              />
            </div>

            {isUnitScope && !fixedUnitId && units.length > 0 && (
              <div className="space-y-2">
                <Label>Unit</Label>
                <Select value={selectedUnitId} onValueChange={setSelectedUnitId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecteer een unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map((unit) => (
                      <SelectItem key={unit.id} value={unit.id}>
                        {unit.boxNumber || unit.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Aantal</Label>
              <Input
                type="number"
                min={1}
                max={10}
                value={quantity}
                onChange={(e) =>
                  setQuantity(
                    Math.min(10, Math.max(1, parseInt(e.target.value) || 1))
                  )
                }
              />
              {quantity > 1 && (
                <p className="text-xs text-muted-foreground">
                  Er worden {quantity} voorzieningen aangemaakt: {name} 1, {name}{" "}
                  2, ...
                </p>
              )}
            </div>

            <div className="flex justify-between">
              <Button
                variant="ghost"
                onClick={() => {
                  setStep("select-type");
                  setSelectedType(null);
                }}
              >
                Terug
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!name || isCreating || isBulkCreating}
              >
                {isCreating || isBulkCreating
                  ? "Aanmaken..."
                  : quantity > 1
                    ? `${quantity} toevoegen`
                    : "Toevoegen"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {conflictData && (
      <TemplateConflictDialog
        open={!!conflictData}
        onOpenChange={(open) => {
          if (!open) setConflictData(null);
        }}
        assetId={conflictData.assetId}
        assetName={conflictData.assetName}
        conflicts={conflictData.conflicts}
        onResolved={() => {
          setConflictData(null);
          onSuccess();
        }}
      />
    )}
    </>
  );
};
